import { Button } from '@components/Button.tsx';
import { ButtonType } from '@common/types/button';
import { IconType } from '@src/IconType.ts';
import { InlineTextEdit } from '@components/InlineTextEdit.tsx';
import { Link, useLoaderData } from 'react-router-dom';
import { type OrganizationDetailsLoaderData } from '@src/features/organizations/views/OrganizationDetailsView.loader';
import { type OrganizationDto } from '@src/api/api.types.ts';
import { Tabs } from '@components/Tabs.tsx';
import { getNewJobWizardUrlWithOrganization, Paths } from '@src/config/paths';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Contact from '@src/features/jobs/views/tabs/Contact';
import Details from '@src/features/organizations/views/tabs/Details';
import Jobs from '@src/features/organizations/views/tabs/Jobs';
import Recruiter from '@src/features/organizations/views/tabs/Recruiter';
import organizationService from '@src/api/services/OrganizationService.ts';
import type { Country } from '@src/api/services/CountryService';
import { Icon } from '@src/components/Icon';

export default function OrganizationDetailsView() {
  const initialOrganization = useLoaderData() as OrganizationDetailsLoaderData;
  const [organization, setOrganization] = useState(initialOrganization);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation('organizationDetails');
  const { t: buttonTranslation } = useTranslation('view', {
    keyPrefix: 'jobs',
  });

  const updateOrganization = useCallback(
    async <Field extends keyof OrganizationDto>(
      fieldIdentifier: Field,
      newValue: OrganizationDto[Field] | null | undefined,
    ): Promise<void> => {
      try {
        setError(null);
        let updatedOrganization: OrganizationDto;
        if (
          newValue === undefined ||
          newValue === '' ||
          (Array.isArray(newValue) && newValue.length === 0)
        ) {
          updatedOrganization = await organizationService.updateOrganization(
            organization.id,
            {
              [fieldIdentifier]: null,
            },
          );
        } else {
          // we can't update the label in the country object, so we just need the id
          if (fieldIdentifier === 'country') {
            newValue = (newValue as Country).id as OrganizationDto[Field];
          }

          updatedOrganization = await organizationService.updateOrganization(
            organization.id,
            {
              [fieldIdentifier]: newValue,
            },
          );
        }
        setOrganization(updatedOrganization);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError(t('errors.unknown', { ns: 'common' }));
        }
      }
    },
    [organization.id, t],
  );

  return (
    <div className="h-full flex flex-col px-3 sm:px-0">
      <div className="w-full sm:bg-white sm:rounded-xl flex items-center sm:mb-8 sm:border-blue-100 sm:border sm:p-6 gap-2 sm:gap-4">
        <Link to={Paths.ORGANIZATIONS} className="p-3 sm:hidden">
          <Icon
            type={IconType.CaretLeft}
            className="size-6"
            aria-hidden="true"
          />
        </Link>
        <InlineTextEdit
          value={organization.name}
          onSubmit={(newName) => updateOrganization('name', newName)}
          ValueComponent={(props) => (
            <p
              {...props}
              className="font-medium text-base sm:text-4xl leading-[2.5rem]"
            />
          )}
          classNameEditingContainer="grow"
          classNameInput="!font-medium text-base sm:!text-4xl leading-[2.5rem]"
          showButtons={false}
          allowEmptyValue={false}
          saveOnBlur
        />
      </div>
      {error && (
        <div className="text-orange-600 my-4">
          {t('errors.errorWhileSaving', { errorMessage: error, ns: 'common' })}
        </div>
      )}
      <Tabs
        tabLevel={0}
        tabs={[
          {
            id: 1,
            title: t('details'),
            content: (
              <Details
                organization={organization}
                setValue={updateOrganization}
              />
            ),
            disabled: false,
            IconLeading: IconType.DocumentText,
            testId: 'details',
            default: true,
          },
          {
            id: 2,
            title: t('recruiter'),
            content: <Recruiter organizationId={organization.id} />,
            IconLeading: IconType.Users,
            testId: 'recruiter',
          },
          {
            id: 3,
            title: t('jobs'),
            content: <Jobs organizationId={organization.id} />,
            IconLeading: IconType.FunnelVertical,
            testId: 'jobs',
          },
          {
            id: 4,
            title: t('contact'),
            content: <Contact organisation={organization.id} />,
            IconLeading: IconType.CalendarBlank,
            testId: 'contact',
            disableScrollingInTab: true,
          },
        ]}
        additionalHeaderNode={
          <Link to={getNewJobWizardUrlWithOrganization(organization.id)}>
            <Button
              label={buttonTranslation('addJob')}
              buttonType={ButtonType.PRIMARY}
              IconLeading={IconType.Add}
              testId={'create-job'}
              className="whitespace-nowrap !p-2 md:!p-3"
            />
          </Link>
        }
      />
    </div>
  );
}
